import { runCvImportWithReview, type CvImportReview } from './cv-import-review';
import { promptCvImportReview } from './cv-import-review-prompt';
import { api } from "./api";
import { Platform } from "react-native";
import { getAPIBaseURL } from "./api-url";
import { appFetch } from "./app-auth";
import type { CvExportFormat } from "./cv-export-policy";
import {
  cvWriteRequestArgs,
  readResponseRevision,
  reportCvReadRevision,
  settleCvWriteResponse,
  type CvWriteOptions,
} from "./cv-write-coordinator";
import {
  buildExportQuery,
  DOWNLOAD_FAILED_MESSAGE,
  downloadAndShareNative,
  getExportFailureMessage,
  parseContentDispositionFilename,
  withAppLocaleDefault,
  type CvDownloadOptions,
} from "./cv-export";
import type {
  CVConfigData,
  ParsedCvData,
  CvSectionId,
  CvHandoffMode,
  CvHandoffStatus,
  CvHandoffSummary,
  ProfilePrivacySettings,
  SmartCVPublicDocument,
  TailoredCVOverrides,
} from "@tabbio/types";

// ─── Types ──────────────────────────────────────────────────────

export type { TailoredCVOverrides };

export interface TailoredCV {
  id: string;
  userId: string;
  /** B01: moves on every content write; sent back as `If-Match` (B02). */
  revision?: number;
  title: string;
  summary: string | null;
  templateId: string;
  sections: unknown;
  isPublic: boolean;
  isDefaultAutoApplyCv?: boolean;
  handoff?: CvHandoffSummary | null;
  slug: string | null;
  createdAt: string;
  updatedAt: string;
}

export type { CvHandoffMode, CvHandoffStatus, CvHandoffSummary };

export interface CvHandoffInput {
  recipientEmail?: string | null;
  recipientName?: string | null;
  mode?: CvHandoffMode;
}

export interface CvHandoffSendInput {
  recipientEmail: string;
  recipientName?: string | null;
  mode?: CvHandoffMode;
}

export interface CvHandoffSendResult {
  handoff: CvHandoffSummary;
  inviteToken: string;
  inviteUrl: string;
}

/**
 * What an `intent: 'update'` claim did to the claimer's MASTER PROFILE, the
 * secondary half of the claim (apps/api CvHandoffProfileUpdateOutcome).
 *
 * The claim itself commits first, so this rides on a SUCCESSFUL claim rather
 * than an error: `'failed'` means the delivered copy is theirs and the invite
 * is spent, but their profile was not written. Never report that as an expired,
 * cancelled or wrong-email invite.
 */
export interface CvHandoffProfileUpdate {
  status: "not_requested" | "applied" | "failed";
  /**
   * Roles the PROFILE write left out because the source stated no start date.
   * The claimed copy still carries them, so this is a note, not a failure.
   */
  skippedExperiences: number;
}

export interface CvHandoffClaimResult {
  handoff: CvHandoffSummary;
  cv: TailoredCV;
  /** Optional: the field is additive, and responses are cast without validation. */
  profileUpdate?: CvHandoffProfileUpdate;
}

/**
 * Fields accepted by PATCH /api/cv/:id. Mirrors the server whitelist
 * (cvUpdateInputSchema). Ownership/visibility fields (userId, isPublic,
 * slug) are intentionally excluded: the server rejects them.
 */
export interface UpdateCVInput {
  title?: string;
  summary?: string | null;
  templateId?: string;
  sections?: unknown;
}

export type ResolvedTailoredCV = SmartCVPublicDocument & {
  cvConfig?: CVConfigData;
  /**
   * Skill-applied section include-list + order (skills two-way design),
   * mirroring `TailoredCVOverrides.sectionsConfig`. null/absent = legacy
   * hardcoded section order.
   */
  sectionsConfig?: { order: CvSectionId[] } | null;
  privacy?: ProfilePrivacySettings;
};

// ─── API Functions ──────────────────────────────────────────────

export async function fetchTailoredCVs(): Promise<TailoredCV[]> {
  const res = await api.get<TailoredCV[]>("/api/cv");
  if (res.error) throw new Error(res.error.message);
  if (!Array.isArray(res.data)) throw new Error("Could not load CVs");
  return res.data;
}

export async function fetchTailoredCV(cvId: string): Promise<TailoredCV> {
  const res = await api.get<TailoredCV>(`/api/cv/${encodeURIComponent(cvId)}`);
  if (res.error || !res.data) throw new Error(res.error?.message || "CV not found");
  return res.data;
}

export async function replaceTailoredCvFromParsed(cvId: string, data: {
  parsed: ParsedCvData;
  importEvidenceId?: string;
  importReview?: CvImportReview;
  expectedUpdatedAt: string;
  replacementConfirmed: true;
}): Promise<TailoredCV> {
  return runCvImportWithReview(data, async (payload) => {
  const res = await api.post<TailoredCV>(`/api/cv/${encodeURIComponent(cvId)}/replace-from-parsed`, payload);
  if (res.error || !res.data) {
    const error = new Error(res.error?.message || "Could not replace CV");
    Object.assign(error, { code: res.error?.code, meta: res.meta });
    throw error;
  }
  return res.data;
  }, promptCvImportReview);
}

export async function fetchResolvedCV(
  cvId: string,
): Promise<ResolvedTailoredCV> {
  const res = await api.get<ResolvedTailoredCV>(`/api/cv/${cvId}/resolved`);
  if (res.error) throw new Error(res.error.message);
  // B02: the revision of this read is the base the editor's saves state.
  reportCvReadRevision(cvId, readResponseRevision(res));
  return res.data ?? ({} as ResolvedTailoredCV);
}

export async function fetchCVOverrides(
  cvId: string,
): Promise<TailoredCVOverrides> {
  const res = await api.get<TailoredCVOverrides>(`/api/cv/${cvId}/overrides`);
  if (res.error) throw new Error(res.error.message);
  // The revision travels in meta only; the overrides body stays a patch base.
  reportCvReadRevision(cvId, readResponseRevision({ data: null, meta: res.meta }));
  return res.data ?? { version: 1 };
}

export async function createTailoredCV(data: {
  title: string;
  templateId?: string;
}): Promise<TailoredCV> {
  const res = await api.post<TailoredCV>("/api/cv", data);
  if (res.error) throw new Error(res.error.message);
  return res.data!;
}

export async function createTailoredCvFromParsed(data: {
  parsed: ParsedCvData;
  importEvidenceId?: string;
  importReview?: CvImportReview;
  title?: string;
}): Promise<TailoredCV> {
  return runCvImportWithReview(data, async (payload) => {
  const res = await api.post<TailoredCV>("/api/cv/from-parsed", payload);
  if (res.error) {
    const error = new Error(res.error.message);
    Object.assign(error, { code: res.error.code, meta: res.meta });
    throw error;
  }
  return res.data!;
  }, promptCvImportReview);
}

export async function createGeneratedTailoredCV(data: {
  title: string;
  prompt: string;
  templateId?: string;
  /**
   * Start from this CV instead of the master profile. The server reads the
   * CV's resolved document, so tailoring an already-tailored CV compounds.
   */
  sourceCvId?: string | null;
}): Promise<TailoredCV> {
  const res = await api.post<TailoredCV>("/api/cv/generate", data);
  if (res.error) throw new Error(res.error.message);
  return res.data!;
}

/**
 * `write` (B02): `ifMatch` states the revision the edit is based on; a stale
 * one throws CvRevisionConflictError and nothing is written. Without it the
 * request is exactly the old one (the server's bounded retry applies).
 */
export async function updateCVOverrides(
  cvId: string,
  patch: Partial<Omit<TailoredCVOverrides, "version">>,
  write?: CvWriteOptions,
): Promise<TailoredCV> {
  const res = await api.patch<TailoredCV>(
    `/api/cv/${cvId}/overrides`,
    patch,
    ...cvWriteRequestArgs(write),
  );
  return settleCvWriteResponse(res, write)!;
}

export async function updateCV(
  cvId: string,
  patch: UpdateCVInput,
  write?: CvWriteOptions,
): Promise<TailoredCV> {
  const res = await api.patch<TailoredCV>(
    `/api/cv/${cvId}`,
    patch,
    ...cvWriteRequestArgs(write),
  );
  return settleCvWriteResponse(res, write)!;
}

export class CvEnhanceApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "CvEnhanceApiError";
  }
}

/**
 * POST /api/cv/:id/enhance — AI-rewrites the SAME CV in place (summary,
 * headline, skills, experience bullets; roles are never deleted). Slow call
 * (10–30s). Errors keep their server code (AI_UNAVAILABLE, NOT_FOUND,
 * VALIDATION_ERROR) so the UI can translate the recoverable ones.
 */
export async function enhanceTailoredCV(cvId: string): Promise<TailoredCV> {
  const res = await api.post<TailoredCV>(`/api/cv/${cvId}/enhance`, {});
  if (res.error) {
    throw new CvEnhanceApiError(res.error.code, res.error.message);
  }
  return res.data!;
}

export async function duplicateTailoredCV(input: {
  cvId: string;
  title?: string;
}): Promise<TailoredCV> {
  const res = await api.post<TailoredCV>(
    `/api/cv/${input.cvId}/duplicate`,
    { title: input.title },
  );
  if (res.error) throw new Error(res.error.message);
  return res.data!;
}

export async function deleteTailoredCV(cvId: string): Promise<void> {
  const res = await api.delete(`/api/cv/${cvId}`);
  if (res.error) throw new Error(res.error.message);
}

export async function createOrUpdateCvHandoffDraft(
  cvId: string,
  input: CvHandoffInput,
): Promise<CvHandoffSummary> {
  const res = await api.post<CvHandoffSummary>(
    `/api/cv/${cvId}/handoff-draft`,
    input,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data!;
}

export async function updateCvHandoff(
  cvId: string,
  input: CvHandoffInput,
): Promise<CvHandoffSummary> {
  const res = await api.patch<CvHandoffSummary>(
    `/api/cv/${cvId}/handoff`,
    input,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data!;
}

export async function sendCvHandoff(
  cvId: string,
  input: CvHandoffSendInput,
): Promise<CvHandoffSendResult> {
  const res = await api.post<CvHandoffSendResult>(
    `/api/cv/${cvId}/handoff/send`,
    input,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data!;
}

export class CvHandoffClaimApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly recipientEmail?: string | null,
  ) {
    super(message);
    this.name = "CvHandoffClaimApiError";
  }
}

/**
 * 'copy' (server default) creates the claimer's own CV copy; 'update' ALSO
 * replaces the claimer's master profile content with the delivered CV,
 * server-side, so their main CV carries it.
 */
export type CvHandoffClaimIntent = "copy" | "update";

/** GET /api/cv/handoffs/invite/:token preview (mutates nothing). */
export interface CvHandoffInvitePreview {
  cvTitle: string;
  senderName: string;
  /**
   * The name a partner goes by when this is a CV they built in the partner
   * studio (LM2), else null. Optional so an older API still previews.
   */
  partnerName?: string | null;
  mode: CvHandoffMode;
  expiresAt: string | null;
}

function toCvHandoffClaimApiError(error: {
  code: string;
  message: string;
  details?: unknown;
}) {
  const details = error.details as { recipientEmail?: unknown } | undefined;
  const recipientEmail =
    typeof details?.recipientEmail === "string"
      ? details.recipientEmail
      : null;
  return new CvHandoffClaimApiError(error.code, error.message, recipientEmail);
}

export async function fetchCvHandoffInvite(
  token: string,
): Promise<CvHandoffInvitePreview> {
  const res = await api.get<CvHandoffInvitePreview>(
    `/api/cv/handoffs/invite/${encodeURIComponent(token)}`,
  );
  if (res.error) {
    // Same guard codes as the claim endpoint, so the claim error screens
    // render preview failures too.
    throw toCvHandoffClaimApiError(res.error);
  }
  return res.data!;
}

export async function claimCvHandoff(
  token: string,
  intent?: CvHandoffClaimIntent,
): Promise<CvHandoffClaimResult> {
  const res = await api.post<CvHandoffClaimResult>(
    "/api/cv/handoffs/claim",
    intent ? { token, intent } : { token },
  );
  if (res.error) {
    throw toCvHandoffClaimApiError(res.error);
  }
  return res.data!;
}

export async function cancelCvHandoff(input: {
  cvId: string;
  handoffId: string;
}): Promise<CvHandoffSummary> {
  const res = await api.post<CvHandoffSummary>(
    `/api/cv/${input.cvId}/handoffs/${input.handoffId}/cancel`,
    {},
  );
  if (res.error) throw new Error(res.error.message);
  return res.data!;
}

// ─── Export / Download ──────────────────────────────────────────

export type ExportFormat = CvExportFormat;

export async function downloadTailoredExport(
  cvId: string,
  format: ExportFormat,
  options?: CvDownloadOptions,
): Promise<void> {
  const baseUrl = getAPIBaseURL();
  const url = `${baseUrl}/api/cv/${cvId}/export/${format}${buildExportQuery(withAppLocaleDefault(options))}`;

  if (Platform.OS === "web") {
    let resp: Response;
    try {
      resp = await appFetch(url, {
        method: "GET",
      });
    } catch {
      throw new Error(DOWNLOAD_FAILED_MESSAGE);
    }
    if (!resp.ok) {
      throw new Error(await getExportFailureMessage(resp));
    }
    const filename = parseContentDispositionFilename(
      resp.headers.get("content-disposition"),
      `cv.${format}`,
    );
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
    return;
  }

  await downloadAndShareNative(url, format, `cv.${format}`);
}
